import { NgModule, provideBrowserGlobalErrorListeners } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ViewportScroller } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouteReuseStrategy } from '@angular/router';
import { QRCodeComponent } from 'angularx-qrcode';

import { AppRoutingModule } from './app-routing-module';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { AppViewportScroller } from './core/services/app-viewport-scroller';
import { App } from './app';
import { PlaygroundReuseStrategy } from './pages/playground/utils/playground-reuse-strategy';
import { Login } from './pages/auth/login/login';
import { Home } from './pages/home/home';
import { Playground } from './pages/playground/playground';
import { Rag } from './pages/playground/rag/rag';
import { Answer } from './pages/playground/answer/answer';
import { Search } from './pages/playground/search/search';
import { SearchBar } from './pages/playground/search/search-bar/search-bar';
import { PromptOptimizer } from './pages/playground/prompt-optimizer/prompt-optimizer';
import { RagResults } from './pages/playground/rag/rag-results/rag-results';
import { FieldTipDirective } from './pages/playground/rag/rag-results/field-tip.directive';
import { ClampOverflowDirective } from './pages/playground/rag/rag-results/clamp-overflow.directive';
import { SplitPane } from './pages/playground/split-pane/split-pane';
import { TopSites } from './pages/playground/top-sites/top-sites';
import { SearchResults } from './pages/playground/search/search-results/search-results';
import { AnswerResults } from './pages/playground/answer/answer-results/answer-results';
import { ImageResults } from './pages/playground/image/image-results/image-results';
import { ImageLightbox } from './pages/playground/image/image-lightbox/image-lightbox';
import { CodeLinesPipe } from './pages/playground/split-pane/code-lines.pipe';
import { Similar } from './pages/playground/similar/similar';
import { Image } from './pages/playground/image/image';
import { Legal } from './pages/playground/legal/legal';
import { Docs } from './pages/docs/overview/docs';
import { DocsQuickstart } from './pages/docs/quickstart/docs-quickstart';
import { DocsAuthentication } from './pages/docs/authentication/docs-authentication';
import { DocsMakingACall } from './pages/docs/making-a-call/docs-making-a-call';
import { DocsPricingLimits } from './pages/docs/pricing-limits/docs-pricing-limits';
import { DocsErrors } from './pages/docs/errors/docs-errors';
import { DocsAccountFilters } from './pages/docs/account-filters/docs-account-filters';
import { DocsApiReferenceIndex } from './pages/docs/api-reference/index/docs-api-reference-index';
import { DocsApiReferenceDetail } from './pages/docs/api-reference/detail/docs-api-reference-detail';
import { DocsParamList } from './pages/docs/api-reference/detail/docs-param-list/docs-param-list';
import { DocsChangelog } from './pages/docs/changelog/docs-changelog';
import { DocsLayout } from './shared/docs-layout/docs-layout';
import { DocsSearch } from './shared/docs-search/docs-search';
import { ApiKeys } from './pages/api-keys/list/api-keys';
import { Billing } from './pages/billing/overview/billing';
import { BillingUsage } from './pages/billing/usage/billing-usage';
import { BillingTransactions } from './pages/billing/transactions/billing-transactions';
import { BillingHeader } from './pages/billing/components/billing-header/billing-header';
import { TxnTable } from './pages/billing/components/txn-table/txn-table';
import { WalletReturn } from './pages/billing/wallet-return/wallet-return';
import { Settings } from './pages/settings/settings';
import { Support } from './pages/support/support';
import { Sidebar } from './shared/sidebar/sidebar';
import { MainLayout } from './shared/main-layout/main-layout';
import { NotificationToast } from './shared/notification-toast/notification-toast';
import { ClickOutsideDirective } from './core/directives/click-outside.directive';
import { CodeHighlightPipe, LatencyPipe, MoneyPipe } from './core/pipes';
import { AuthPanel } from './shared/auth-panel/auth-panel';
import { Skeleton } from './shared/skeleton/skeleton';
import { Sparkline } from './shared/charts/sparkline/sparkline';
import { LineChart } from './shared/charts/line-chart/line-chart';
import { BarList } from './shared/charts/bar-list/bar-list';
import { StatTile } from './shared/stat-tile/stat-tile';
import { SectionIcon } from './shared/section-icon/section-icon';
import { WalletSummaryCard } from './pages/home/components/wallet-summary-card/wallet-summary-card';
import { UsageChartCard } from './pages/home/components/usage-chart-card/usage-chart-card';
import { TopEndpointsCard } from './pages/home/components/top-endpoints-card/top-endpoints-card';
import { ApiKeysSummaryCard } from './pages/home/components/api-keys-summary-card/api-keys-summary-card';
import { RecentActivityCard } from './pages/home/components/recent-activity-card/recent-activity-card';
import { Pages } from './pages/playground/pages/pages';
// MapAreaPicker (pages/playground/map-area-picker) is deliberately NOT
// declared here it's standalone and loaded via dynamic import() from
// rag.ts/search.ts so OpenLayers stays out of the eager bundle. See its
// own file for why.
import { Signup } from './pages/auth/signup/signup';
import { ConfirmEmail } from './pages/auth/confirm-email/confirm-email';
import { CheckEmail } from './pages/auth/check-email/check-email';
import { ForgotPassword } from './pages/auth/forgot-password/forgot-password';
import { ResetPassword } from './pages/auth/reset-password/reset-password';
import { MilaMultiSelect } from './shared/mila-multi-select/mila-multi-select';
import { MilaMultiSelectHierarchy } from './shared/mila-multi-select-hierarchy/mila-multi-select-hierarchy';
import { MilaMultiSelectTreeItem } from './shared/mila-multi-select-hierarchy/mila-multi-select-tree-item/mila-multi-select-tree-item';
import { AdminLayout } from './pages/admin/admin-layout/admin-layout';
import { AdminOverview } from './pages/admin/overview/admin-overview';
import { PlatformStatsRow } from './pages/admin/overview/components/platform-stats-row/platform-stats-row';
import { UsageSpendCard } from './pages/admin/overview/components/usage-spend-card/usage-spend-card';
import { OrganizationsPreviewCard } from './pages/admin/overview/components/organizations-preview-card/organizations-preview-card';
import { CallsPreviewCard } from './pages/admin/overview/components/calls-preview-card/calls-preview-card';
import { TransactionsPreviewCard } from './pages/admin/overview/components/transactions-preview-card/transactions-preview-card';
import { UsersPreviewCard } from './pages/admin/overview/components/users-preview-card/users-preview-card';
import { AdminOrganizations } from './pages/admin/organizations/list/admin-organizations';
import { AdminOrganizationDetail } from './pages/admin/organizations/detail/admin-organization-detail';
import { AdminKeyCallsModal } from './pages/admin/organizations/detail/key-calls-modal/admin-key-calls-modal';
import { AdminCalls } from './pages/admin/calls/admin-calls';
import { AdminTransactions } from './pages/admin/transactions/admin-transactions';
import { AdminUsers } from './pages/admin/users/admin-users';
import { AdminTotalsCard } from './pages/admin/totals-card/admin-totals-card';
import { AdminTotalsFigure } from './pages/admin/totals-card/admin-totals-figure';
import { MODAL_LAYOUT } from './shared/modal';
import { AvatarModal } from './pages/settings/modals/avatar/avatar-modal';
import { BackupCodesModal } from './pages/settings/modals/backup-codes/backup-codes-modal';
import { ChangePasswordModal } from './pages/settings/modals/change-password/change-password-modal';
import { DisableTwoFactorModal } from './pages/settings/modals/disable-two-factor/disable-two-factor-modal';
import { TwoFactorSetupModal } from './pages/settings/modals/two-factor-setup/two-factor-setup-modal';

@NgModule({
  declarations: [
    App,
    Login,
    Home,
    Playground,
    Rag,
    Answer,
    Search,
    SearchBar,
    PromptOptimizer,
    RagResults,
    FieldTipDirective,
    ClampOverflowDirective,
    SplitPane,
    TopSites,
    SearchResults,
    AnswerResults,
    ImageResults,
    ImageLightbox,
    CodeLinesPipe,
    Similar,
    Image,
    Legal,
    Docs,
    DocsQuickstart,
    DocsAuthentication,
    DocsMakingACall,
    DocsPricingLimits,
    DocsErrors,
    DocsAccountFilters,
    DocsApiReferenceIndex,
    DocsApiReferenceDetail,
    DocsParamList,
    DocsChangelog,
    DocsLayout,
    DocsSearch,
    ApiKeys,
    Billing,
    BillingUsage,
    BillingTransactions,
    BillingHeader,
    TxnTable,
    WalletReturn,
    Settings,
    AvatarModal,
    ChangePasswordModal,
    TwoFactorSetupModal,
    BackupCodesModal,
    DisableTwoFactorModal,
    Support,
    Sidebar,
    MainLayout,
    Pages,
    Signup,
    ConfirmEmail,
    CheckEmail,
    ForgotPassword,
    ResetPassword,
    NotificationToast,
    ClickOutsideDirective,
    MoneyPipe,
    LatencyPipe,
    CodeHighlightPipe,
    AuthPanel,
    Skeleton,
    Sparkline,
    LineChart,
    BarList,
    StatTile,
    SectionIcon,
    WalletSummaryCard,
    UsageChartCard,
    TopEndpointsCard,
    ApiKeysSummaryCard,
    RecentActivityCard,
    MilaMultiSelect,
    MilaMultiSelectHierarchy,
    MilaMultiSelectTreeItem,
    AdminLayout,
    AdminOverview,
    PlatformStatsRow,
    UsageSpendCard,
    OrganizationsPreviewCard,
    CallsPreviewCard,
    TransactionsPreviewCard,
    UsersPreviewCard,
    AdminOrganizations,
    AdminOrganizationDetail,
    AdminKeyCallsModal,
    AdminCalls,
    AdminTransactions,
    AdminUsers,
    AdminTotalsCard,
    AdminTotalsFigure,
  ],
  imports: [BrowserModule, AppRoutingModule, HttpClientModule, FormsModule, ReactiveFormsModule, QRCodeComponent, ...MODAL_LAYOUT],
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    // Retargets Angular's router-driven scroll restoration from the window
    // (which never scrolls in this app see styles.scss `body { overflow:
    // hidden }`) to .app__main, the real scroll container. See
    // core/services/app-viewport-scroller.ts.
    { provide: ViewportScroller, useExisting: AppViewportScroller },
    { provide: RouteReuseStrategy, useClass: PlaygroundReuseStrategy },
  ],
  bootstrap: [App],
})
export class AppModule {}
